/*
 * sticky-media-preview.js (Madson custom)
 * Mobile-only (<750px) sticky strip pinned under the header on PDPs.
 * Shows the currently selected variant image + product title + price once
 * the main media gallery scrolls out of view. Tapping the strip smooth-
 * scrolls back to the gallery top.
 *
 * Loaded only when the product-information section setting
 * `mobile_sticky_media_preview` is enabled — the script is inert otherwise
 * (no matching [data-sticky-media-preview] element exists).
 *
 * Robustness notes:
 * - Horizon REPLACES the entire <media-gallery> element on variant change
 *   (assets/media-gallery.js #handleVariantUpdate → replaceWith). So we
 *   can't hold a reference: a section-scoped MutationObserver re-binds the
 *   IntersectionObserver to the new gallery and re-mirrors the active image.
 * - Slide changes flip `aria-hidden` on <slideshow-slide> elements; the
 *   same MutationObserver (attributeFilter) catches swipes/thumbnail taps.
 * - Header offset: the strip pins to the live bottom edge of
 *   #header-component so it stacks under sticky headers ('always' and
 *   'scroll-up' modes) and sits at top:0 when the header has scrolled away.
 */
(function () {
  'use strict';

  var MOBILE_MQ = '(max-width: 749px)';
  var INIT_FLAG = 'data-smp-init';

  function setup(strip) {
    if (strip.getAttribute(INIT_FLAG) === '1') return;
    strip.setAttribute(INIT_FLAG, '1');

    var section = strip.closest('.shopify-section');
    if (!section) return;

    var mql = window.matchMedia(MOBILE_MQ);
    var stripImage = strip.querySelector('[data-smp-image]');
    var stripPrice = strip.querySelector('[data-smp-price]');

    var shown = false;
    var galleryOutOfView = false;
    var observedGallery = null;
    var io = null;
    var syncRaf = null;
    var scrollRaf = null;
    var lastSrc = '';

    function currentGallery() {
      return section.querySelector('media-gallery');
    }

    function headerEl() {
      return document.getElementById('header-component') || document.querySelector('.header-section');
    }

    // Visible bottom edge of the header right now (0 when scrolled away).
    function headerBottom() {
      var header = headerEl();
      if (!header) return 0;
      var rect = header.getBoundingClientRect();
      return Math.max(0, rect.bottom);
    }

    function updateTop() {
      strip.style.top = headerBottom() + 'px';
    }

    function setShown(next) {
      if (next === shown) {
        if (shown) updateTop();
        return;
      }
      shown = next;
      if (next) updateTop();
      strip.setAttribute('data-shown', next ? 'true' : 'false');
    }

    function evaluate() {
      setShown(mql.matches && galleryOutOfView);
    }

    function observeGallery() {
      var gallery = currentGallery();
      if (gallery === observedGallery) return;
      if (io) {
        io.disconnect();
        io = null;
      }
      observedGallery = gallery;
      if (!gallery) {
        galleryOutOfView = false;
        evaluate();
        return;
      }
      io = new IntersectionObserver(function (entries) {
        var entry = entries[entries.length - 1];
        if (!entry) return;
        galleryOutOfView = !entry.isIntersecting;
        evaluate();
      });
      io.observe(gallery);
    }

    // Mirror the active slide's image into the strip. The active slide is
    // slideshow-slide[aria-hidden="false"]; fall back to the first media
    // image so video/3D-model slides keep the last good product shot.
    function syncImage() {
      if (!stripImage) return;
      var img =
        section.querySelector('media-gallery slideshow-slide[aria-hidden="false"] img') ||
        section.querySelector('media-gallery slideshow-slide img') ||
        section.querySelector('media-gallery img');
      if (!img) return;
      var src = img.currentSrc || img.src;
      if (!src || src === lastSrc) return;
      lastSrc = src;
      stripImage.src = src;
      if (img.alt) stripImage.alt = img.alt;
    }

    // Mirror the current price from the section's price block (morphed by
    // Horizon on variant change); fall back to the sticky ATC bar's price.
    function syncPrice() {
      if (!stripPrice) return;
      var source =
        section.querySelector('product-price .price') ||
        section.querySelector('.sticky-add-to-cart__price .price');
      if (!source) return;
      var text = (source.textContent || '').trim();
      if (text && text !== stripPrice.textContent) {
        stripPrice.textContent = text;
      }
    }

    function sync() {
      observeGallery();
      syncImage();
      syncPrice();
    }

    function scheduleSync() {
      if (syncRaf) return;
      syncRaf = requestAnimationFrame(function () {
        syncRaf = null;
        sync();
      });
    }

    // One observer covers: variant changes (media-gallery / product-price
    // get replaced or morphed → childList), slide changes (aria-hidden),
    // and image src swaps.
    var mo = new MutationObserver(scheduleSync);
    mo.observe(section, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'src', 'srcset'],
    });

    // Keep the strip glued to the header's bottom edge while visible
    // (handles 'scroll-up' sticky headers sliding in and out).
    function onScrollOrResize() {
      if (!shown || scrollRaf) return;
      scrollRaf = requestAnimationFrame(function () {
        scrollRaf = null;
        updateTop();
      });
    }
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });

    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', evaluate);
    } else if (typeof mql.addListener === 'function') {
      mql.addListener(evaluate);
    }

    // Tap → smooth-scroll back to the top of the gallery, clearing any
    // sticky header so the gallery isn't hidden underneath it.
    strip.addEventListener('click', function () {
      var gallery = currentGallery();
      if (!gallery) return;
      var offset = 0;
      var stickyHeader = document.querySelector('#header-component[sticky]');
      if (stickyHeader) {
        var v = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-group-height'));
        offset = isNaN(v) ? stickyHeader.getBoundingClientRect().height : v;
      }
      var top = gallery.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    });

    // Teardown when the section is removed (theme editor re-renders replace the
    // whole section). The strip is a plain element, not a custom element, so
    // there's no disconnectedCallback — hook shopify:section:unload instead and
    // release the window/matchMedia listeners + both observers so they don't
    // leak against a detached DOM.
    function teardown() {
      if (io) {
        io.disconnect();
        io = null;
      }
      if (mo) {
        mo.disconnect();
      }
      if (syncRaf) {
        cancelAnimationFrame(syncRaf);
        syncRaf = null;
      }
      if (scrollRaf) {
        cancelAnimationFrame(scrollRaf);
        scrollRaf = null;
      }
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      if (typeof mql.removeEventListener === 'function') {
        mql.removeEventListener('change', evaluate);
      } else if (typeof mql.removeListener === 'function') {
        mql.removeListener(evaluate);
      }
      document.removeEventListener('shopify:section:unload', onUnload);
    }

    function onUnload(event) {
      if (event.target && event.target.contains(strip)) teardown();
    }
    document.addEventListener('shopify:section:unload', onUnload);

    sync();
    evaluate();
  }

  function init() {
    var strips = document.querySelectorAll('[data-sticky-media-preview]');
    for (var i = 0; i < strips.length; i++) {
      setup(strips[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: section re-renders replace the DOM.
  document.addEventListener('shopify:section:load', init);
})();
